/** @jsxImportSource @emotion/react */
"use client";

import * as React from "react";
import { css, type Interpolation } from "@emotion/react";
import { type Theme } from "../../styles";
import * as RechartsPrimitive from "recharts";

type ChartConfig = Record<string, { color?: string; label?: string; [key: string]: unknown }>;

const chartContainerStyles = css`
  display: flex;
  aspect-ratio: 16 / 9;
  justify-content: center;
  font-size: 0.75rem;

  & .recharts-cartesian-axis-tick text {
    fill: var(--color-muted-foreground);
  }

  & .recharts-cartesian-grid line[stroke="#ccc"] {
    stroke: color-mix(in srgb, var(--color-border) 50%, transparent);
  }

  & .recharts-curve.recharts-tooltip-cursor {
    stroke: var(--color-border);
  }

  & .recharts-dot[stroke="#fff"] {
    stroke: transparent;
  }

  & .recharts-layer {
    outline: none;
  }

  & .recharts-polar-grid [stroke="#ccc"] {
    stroke: var(--color-border);
  }

  & .recharts-radial-bar-background-sector {
    fill: var(--color-muted);
  }

  & .recharts-rectangle.recharts-tooltip-cursor {
    fill: var(--color-muted);
  }

  & .recharts-reference-line [stroke="#ccc"] {
    stroke: var(--color-border);
  }

  & .recharts-sector[stroke="#fff"] {
    stroke: transparent;
  }

  & .recharts-sector {
    outline: none;
  }

  & .recharts-surface {
    outline: none;
  }
`;

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
    css?: Interpolation<Theme>;
  }
>(({ id, className, children, config, css: cssProp, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <div
      data-chart={chartId}
      ref={ref}
      css={[chartContainerStyles, cssProp]}
      className={className}
      {...props}
    >
      <ChartStyle id={chartId} config={config} />
      <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
    </div>
  );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(([, c]) => c.color);
  if (!colorConfig.length) return null;
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
[data-chart="${id}"] {
${colorConfig.map(([key, itemConfig]) => `  --color-${key}: ${itemConfig.color};`).join("\n")}
}
`,
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const tooltipContentStyles = css`
  display: grid;
  min-width: 8rem;
  align-items: flex-start;
  gap: 0.375rem;
  border-radius: 0.5rem;
  border: 1px solid color-mix(in srgb, var(--color-border) 50%, transparent);
  background-color: var(--color-popover);
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
`;

const tooltipLabelStyles = css`
  font-weight: 500;
`;

const tooltipItemsStyles = css`
  display: grid;
  gap: 0.375rem;
`;

const tooltipItemStyles = css`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.5rem;

  & > svg {
    height: 0.625rem;
    width: 0.625rem;
    color: var(--color-muted-foreground);
  }
`;

const tooltipItemCenterStyles = css`
  ${tooltipItemStyles}
  align-items: center;
`;

const indicatorDotStyles = css`
  flex-shrink: 0;
  border-radius: 2px;
  height: 0.625rem;
  width: 0.625rem;
`;

const indicatorLineStyles = css`
  flex-shrink: 0;
  border-radius: 2px;
  width: 0.25rem;
`;

const indicatorDashedStyles = css`
  flex-shrink: 0;
  border-radius: 2px;
  width: 0;
  border: 1.5px dashed;
  background-color: transparent;
  margin: 0.125rem 0;
`;

const tooltipItemContentStyles = css`
  display: flex;
  flex: 1;
  justify-content: space-between;
  line-height: 1;
  align-items: center;
`;

const tooltipItemContentEndStyles = css`
  ${tooltipItemContentStyles}
  align-items: flex-end;
`;

const tooltipItemNameStyles = css`
  color: var(--color-muted-foreground);
`;

const tooltipItemValueStyles = css`
  font-family: monospace;
  font-weight: 500;
  tabular-nums;
  color: var(--color-popover-foreground);
`;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: "line" | "dot" | "dashed";
      css?: Interpolation<Theme>;
      payload?: Array<Record<string, unknown>>;
      label?: string;
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      color,
      css: cssProp,
    },
    ref
  ) => {
    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) return null;
      const [item] = payload;
      const value = typeof label === "string" ? label : (item as Record<string, unknown>).name;
      if (labelFormatter) return labelFormatter(label as string, payload as never);
      return value;
    }, [label, labelFormatter, payload, hideLabel]);

    if (!active || !payload?.length) return null;
    const nestLabel = payload.length === 1 && indicator !== "dot";

    return (
      <div ref={ref} css={[tooltipContentStyles, cssProp]} className={className}>
        {!nestLabel ? (
          <div
            css={[
              tooltipLabelStyles,
              labelClassName &&
                css`
                  ${labelClassName}
                `,
            ]}
          >
            {tooltipLabel as React.ReactNode}
          </div>
        ) : null}
        <div css={tooltipItemsStyles}>
          {payload.map((item: Record<string, unknown> & { payload?: Record<string, unknown>; color?: string; name?: string; value?: unknown }) => {
            const indicatorColor = (color || item.payload?.fill || item.color) as string | undefined;
            const indicatorStyles =
              indicator === "dot"
                ? indicatorDotStyles
                : indicator === "line"
                  ? indicatorLineStyles
                  : indicatorDashedStyles;
            const itemStyles = indicator === "dot" ? tooltipItemCenterStyles : tooltipItemStyles;
            const ind = indicator as string;
            const contentStyles =
              nestLabel && ind !== "dot"
                ? tooltipItemContentEndStyles
                : tooltipItemContentStyles;

            return (
              <div key={String(item.dataKey ?? item.name)} css={itemStyles}>
                {!hideIndicator && (
                  <div
                    css={indicatorStyles}
                    style={{
                      backgroundColor: indicatorColor,
                      borderColor: indicatorColor,
                    }}
                  />
                )}
                <div css={contentStyles}>
                  <div css={tooltipItemsStyles}>
                    {nestLabel ? (
                      <div
                        css={[
                          tooltipLabelStyles,
                          labelClassName &&
                            css`
                              ${labelClassName}
                            `,
                        ]}
                      >
                        {tooltipLabel as React.ReactNode}
                      </div>
                    ) : null}
                    <div css={tooltipItemNameStyles}>{item.name}</div>
                  </div>
                  {item.value != null && <div css={tooltipItemValueStyles}>{String(item.value)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltipContent";

export { ChartContainer, ChartTooltip, ChartTooltipContent };
